import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
  WritableSignal,
  DestroyRef
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {QuoteService} from "../../../services/quote.service";
import {JobsService} from "../../../services/jobs.service";
import {ActivatedRoute, Router} from "@angular/router";
import {SessionService} from "../../../services/session.service";
import {ToastService} from "../../../services/toast.service";
import {DateUtils} from "../../../utils/date-utils";
import {FormBuilder, FormGroup} from "@angular/forms";
import {TableData} from "../../../interfaces/table/table-data";
import {auditTime, catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap, tap} from "rxjs";
import {TableParams} from "../../../interfaces/table/table-params";
import {ObjectUtils} from "../../../utils/object-utils";
import {StatusCategory} from "../../../enums/status-category";
import {constructQuotesColumns, QuoteTableColumn} from "./quotes-table.config";

@Component({
  selector: 'app-quotes',
  templateUrl: './quotes.component.html',
  styleUrls: ['./quotes.component.css'],
  standalone: false
})
export class QuotesComponent implements OnInit {
  currentUser = signal<any>(null);
  readonly allLocationsOption = {location_id: 0, location_name: 'All Locations'};
  locations: any[] = [this.allLocationsOption];
  readonly allJobType = {job_type_id: 0, job_type_name: 'All Job Types'};
  jobTypes: any[] = [this.allJobType];
  readonly allQuoteStatus = {quote_status_id: 0, display_name: 'All Quote Statuses'};
  quoteStatuses: any[] = [this.allQuoteStatus];

  filterForm!: FormGroup;
  tab = signal<'pending' | 'completed'>('pending');
  searching = signal(false);
  currentFileType: 'csv' | 'excel' | null = null;
  isExportDropdownOpen: boolean = false;
  isGeneratingReport: boolean = false;
  readonly unexpandedAssetsCount = 3;
  expandedAssets = new Set<string>();

  quotesTableColumns = computed(() => {
    const showCustomFields = this.currentUser()?.show_custom_fields;
    return constructQuotesColumns(this.quoteNumberColumn, this.assetColumn, showCustomFields);
  });
  @ViewChild('quoteNumberColumn', {static: true}) quoteNumberColumn!: TemplateRef<any>;
  @ViewChild('assetColumn', {static: true}) assetColumn!: TemplateRef<any>;

  pendingTableData = signal<TableData>({data: [], total: 0});
  loadingPendingTableData = signal<boolean>(true);
  loadPendingQuotes$ = new Subject<{ tableParams: TableParams, filterParams: any }>();
  pendingTableParams: TableParams = {currentPage: 1, pageSize: 10, sortParams: null};
  currentPendingTablePage = signal<number>(1);

  completedTableData = signal<TableData>({data: [], total: 0});
  loadingCompletedTableData = signal<boolean>(true);
  loadCompletedQuotes$ = new Subject<{ tableParams: TableParams, filterParams: any }>();
  completedTableParams: TableParams = {currentPage: 1, pageSize: 10, sortParams: null};
  currentCompletedTablePage = signal<number>(1);

  protected readonly QuoteTableColumn = QuoteTableColumn;
  private readonly jobService = inject(JobsService);
  private readonly quoteService = inject(QuoteService);
  private readonly router = inject(Router);
  private readonly activeRouter = inject(ActivatedRoute);
  private readonly sessionService = inject(SessionService);
  private readonly fb = inject(FormBuilder);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.sessionService.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.currentUser.set(res.user));
  }

  ngOnInit() {
    this.initFilterForm();

    this.initQuotesObservable(
      this.loadPendingQuotes$,
      this.pendingTableData,
      this.loadingPendingTableData,
      StatusCategory.Pending
    );
    this.initQuotesObservable(
      this.loadCompletedQuotes$,
      this.completedTableData,
      this.loadingCompletedTableData,
      StatusCategory.Completed
    );

    this.getJobTypes();
    this.getJobStatuses();
    this.getLocations();

    this.refreshData();
  }

  getJobTypes() {
    this.jobService.getJobTypes().then((types: any) => {
      this.jobTypes = [this.allJobType, ...types];
    });
  }

  getJobStatuses() {
    this.quoteService.getQuoteStatus().then((statuses: any) => {
      this.quoteStatuses = [this.allQuoteStatus, ...statuses];

      const queryStatusFilterName = this.activeRouter.snapshot.queryParams['status'];
      if (!queryStatusFilterName) return;

      const status = statuses.find((item: any) =>
        item.display_name.replaceAll(' ', '_').toLowerCase().includes(queryStatusFilterName));
      if (status) {
        this.filterForm.get('status')!.setValue(status.quote_status_id);
      }
    });
  }

  getLocations() {
    this.jobService.getLocations().then((locations: any) => {
      this.locations = [this.allLocationsOption, ...locations];
    });
  }

  initFilterForm() {
    this.filterForm = this.fb.group({
      search: [''],
      type: [0],
      status: [0],
      location: [0]
    });

    this.filterForm.get('search')!.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.currentPendingTablePage.set(1);
      this.currentCompletedTablePage.set(1);
      this.pendingTableParams = {...this.pendingTableParams, currentPage: 1};
      this.completedTableParams = {...this.completedTableParams, currentPage: 1};
      this.refreshData();
    });

    this.filterForm.valueChanges.pipe(
      distinctUntilChanged((prev, curr) =>
        prev.type === curr.type && prev.status === curr.status && prev.location === curr.location),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      const selectedStatus = this.quoteStatuses.find(status =>
        status.quote_status_id === this.filterForm.get('status')!.value);
      if (selectedStatus?.status_category_id === StatusCategory.Completed) {
        this.tab.set('completed');
      } else if (selectedStatus?.status_category_id === StatusCategory.Pending) {
        this.tab.set('pending');
      }
      this.currentPendingTablePage.set(1);
      this.currentCompletedTablePage.set(1);
      this.pendingTableParams = {...this.pendingTableParams, currentPage: 1};
      this.completedTableParams = {...this.completedTableParams, currentPage: 1};
      this.refreshData();
    });
  }

  initQuotesObservable(
    subject: Subject<{ tableParams: TableParams, filterParams: any }>,
    tableDataSignal: WritableSignal<TableData>,
    loadingSignal: WritableSignal<boolean>,
    statusCategoryId: string) {
    subject.pipe(
      distinctUntilChanged((prev, curr) => ObjectUtils.deepEqual(prev, curr)),
      tap(_ => loadingSignal.set(true)),
      auditTime(0),
      switchMap(params => {
        const {tableParams, filterParams} = params;
        const offset = (tableParams.currentPage - 1) * tableParams.pageSize;
        return this.quoteService.getQuotes(tableParams.pageSize, offset, filterParams.search, tableParams.sortParams,
          filterParams.type, filterParams.status, statusCategoryId, filterParams.location);
      }),
      catchError(() => {
        loadingSignal.set(false);
        this.toastService.showToast('Failed to load service schedule', 'error');
        return of({status: 'error', data: {Data: []}, errors: []});
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response: any) => {
      const data = response;
      if (!Array.isArray(data)) {
        console.error('API returned non-array data:', data);
        tableDataSignal.set({data: [], total: 0});
        return;
      }

      const quotes = data.map((item: any) => {
        let po_number = null;
        if (item.custom_values != null) {
          const custom_fields = JSON.parse(item.custom_values);
          po_number = custom_fields.find((x: any) => x.field_name == 'pick_ticket_number')?.value;
        }
        return {
          quote_number: item.quote_number,
          quote_status: {name: item.status, color: item.color},
          quote_total: this.formatMoney(item.quote_total),
          job_numbers: item.job_numbers,
          job_type: item.job_type,
          asset: item.assets,
          location_name: item.location_name,
          expiration_dt: DateUtils.formatDate(item.expiration_dt),
          po_number: po_number,
          order_number: item.order_number,
          renew_pick_ticket_number: item.renew_pick_ticket_number
        };
      });

      const totalCount = data.length > 0 ? data[0].total_count : 0;
      tableDataSignal.set({data: quotes, total: totalCount});
      loadingSignal.set(false);
    });
  }

  formatMoney(money: any) {
    if (money == null || isNaN(Number(money))) {
      return '--';
    }
    const amount = Number(money);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  refreshData() {
    const filterParams = {...this.filterForm.value};
    this.searching.set(!!filterParams.search.trim());
    if (this.tab() === 'pending') {
      const tableParams = {...this.pendingTableParams};
      this.loadPendingQuotes$.next({tableParams, filterParams});
    } else {
      const tableParams = {...this.completedTableParams};
      this.loadCompletedQuotes$.next({tableParams, filterParams});
    }
  }

  changeTab(newTab: 'pending' | 'completed') {
    this.tab.set(newTab);
    const category = newTab === 'pending' ? StatusCategory.Pending : StatusCategory.Completed;
    const selectedStatus = this.quoteStatuses.find(status =>
      status.quote_status_id === this.filterForm.get('status')!.value);
    if (selectedStatus?.status_category_id && selectedStatus.status_category_id !== category) {
      this.filterForm.get('status')!.setValue(0);
    }
    this.refreshData();
  }

  onRowClick(row: any) {
    this.router.navigate(['/quote'], {queryParams: {quote_id: row.quote_number}});
  }

  onPendingTableParamsChange(params: TableParams) {
    this.pendingTableParams = params;
    const tableParams = {...params};
    const filterParams = {...this.filterForm.value};
    this.loadPendingQuotes$.next({tableParams, filterParams});
  }

  onCompletedTableParamsChange(params: TableParams) {
    this.completedTableParams = params;
    const tableParams = {...params};
    const filterParams = {...this.filterForm.value};
    this.loadCompletedQuotes$.next({tableParams, filterParams});
  }

  isSelectClearable(controlName: string) {
    return this.filterForm.get(controlName)?.value !== 0;
  }

  clearSelectValue(controlName: string) {
    this.filterForm.get(controlName)?.setValue(0);
  }

  toggleExportDropdown() {
    this.isExportDropdownOpen = !this.isExportDropdownOpen;
  }

  closeExportDropdown() {
    this.isExportDropdownOpen = false;
  }

  async handleExport(format: 'csv' | 'excel') {
    const currentTable = this.tab() === 'pending' ? this.pendingTableData() : this.completedTableData();
    const anyQuotes = this.searching()
      ? (currentTable.total ?? 0) > 0
      : this.pendingTableData().total! > 0 || this.completedTableData().total! > 0;
    if (!anyQuotes) {
      this.toastService.showToast("No Quotes were found that match this filter", "error");
      return;
    }

    this.isExportDropdownOpen = false;
    this.isGeneratingReport = true;
    this.currentFileType = format;

    const filters = this.filterForm.value;
    try {
      const response: any = await this.quoteService.getQuoteExport(
        filters.type,
        filters.status,
        filters.location,
        filters.search,
        format
      );

      const downloadUrl = response?.downloadUrl;
      if (!downloadUrl) {
        this.toastService.showToast('Failed to generate the report. Please try again later.', 'error');
        return;
      }
      window.open(downloadUrl, '_blank');
    } catch (error) {
      this.toastService.showToast('Failed to generate the report. Please try again later.', 'error');
    } finally {
      this.isGeneratingReport = false;
    }
  }

  getVisibleAssets(items: any[]): any[] {
    return items.slice(0, this.unexpandedAssetsCount);
  }

  getHiddenAssets(items: any[]): any[] {
    return items.slice(this.unexpandedAssetsCount);
  }

  toggleExpandedAssets(rowId: string, value: boolean) {
    if (value) {
      this.expandedAssets.delete(rowId);
    } else {
      this.expandedAssets.add(rowId);
    }
  }

  isAssetExpanded(rowId: string): boolean {
    return this.expandedAssets.has(rowId);
  }
}
